'use client';

import { selectCurrentAccountId, selectRootAccounts } from '@/lib/redux/app.selectors';
import { setCurrentAccountId } from '@/lib/redux/app.slice';
import { useAppDispatch } from '@/lib/redux/store';
import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useSelector } from 'react-redux';

// localStorage key for storing the user's account selection
const SELECTED_ACCOUNT_STORAGE_KEY = 'selectedAccountId';

/**
 * Hook to initialize the current account ID from rootAccounts on app load
 * This ensures that even when navigating directly to a deep URL,
 * the account context is properly initialized.
 */
const useInitializeCurrentAccount = () => {
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const rootAccounts = useSelector(selectRootAccounts);
  const currentAccountId = useSelector(selectCurrentAccountId);

  useEffect(() => {
    // Skip if we already have a current account or no root accounts yet
    if (currentAccountId || !rootAccounts || rootAccounts.length === 0) {
      return;
    }

    // Priority order for selecting account:
    // 1. URL query parameter (?account=xxx)
    // 2. localStorage (previous selection)
    // 3. First root account (default fallback)

    let selectedAccountId: string | null = null;

    // 1. Check URL query parameter first
    const urlAccountId = searchParams.get('account');
    if (urlAccountId) {
      selectedAccountId = urlAccountId;
    }

    // 2. Check localStorage for previous selection
    if (!selectedAccountId) {
      try {
        const storedAccountId = localStorage.getItem(SELECTED_ACCOUNT_STORAGE_KEY);
        if (storedAccountId) {
          selectedAccountId = storedAccountId;
        }
      } catch (error) {
        console.warn('Error reading from localStorage:', error);
      }
    }

    // 3. Default to first root account
    if (!selectedAccountId) {
      selectedAccountId = rootAccounts[0];
    }

    // Set the current account ID in Redux
    if (selectedAccountId) {
      dispatch(setCurrentAccountId(selectedAccountId));
    }
  }, [rootAccounts, currentAccountId, searchParams, dispatch]);
};

export default useInitializeCurrentAccount;
